import { useEffect, useRef } from "react";
import { TIER_COLORS, BACKEND_URL, tierImage } from "@/lib/api";

function GiftIcon({ a, className, emojiClass, fallback = "🎁", style }) {
  if (a.icon_url) return <img src={`${BACKEND_URL}${a.icon_url}`} alt={a.name} className={className} style={style} />;
  return <span className={emojiClass} style={style}>{a.emoji || fallback}</span>;
}

function GiftVideoFx({ a, onDone }) {
  const ref = useRef(null);

  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    v.muted = false;
    v.volume = 0.9;
    v.play().catch(() => {
      v.muted = true;
      v.play().catch(() => {});
    });
  }, []);

  return (
    <div className="absolute inset-0 flex items-center justify-center" data-testid="gift-video-fx">
      <video
        ref={ref}
        src={`${BACKEND_URL}${a.video_url}`}
        autoPlay playsInline
        onEnded={() => onDone(a.key)}
        onError={() => onDone(a.key)}
        className="absolute inset-0 w-full h-full object-contain opacity-70"
      />
      <div className="absolute bottom-24 left-0 right-0 flex flex-col items-center gift-pop">
        {a.icon_url && <img src={`${BACKEND_URL}${a.icon_url}`} alt={a.name} className="w-16 h-16 object-contain mb-1 drop-shadow-[0_0_20px_rgba(236,72,153,0.8)]" />}
        <span className="gradient-text text-xl font-extrabold font-display drop-shadow-[0_0_20px_rgba(236,72,153,0.8)]">
          {!a.icon_url && (a.emoji || "🎬")} {a.name} {a.combo > 1 ? `x${a.combo}` : ""}
        </span>
        <span className="text-white/90 text-sm font-semibold">from {a.senderName}</span>
      </div>
    </div>
  );
}

export function GiftAnimationLayer({ animations, onDone }) {
  return (
    <div className="absolute inset-0 pointer-events-none z-30 overflow-hidden">
      {animations.map((a) => {
        if (a.video_url) {
          return <GiftVideoFx key={a.key} a={a} onDone={onDone} />;
        }
        if (a.tier === "medium") {
          return (
            <div key={a.key} className="absolute inset-0 flex items-center justify-center">
              <div className="gift-pop flex flex-col items-center">
                <GiftIcon a={a} className="w-28 h-28 object-contain drop-shadow-[0_0_25px_rgba(236,72,153,0.7)]" emojiClass="text-7xl drop-shadow-[0_0_25px_rgba(236,72,153,0.7)]" />
                <span className="text-white text-sm font-bold mt-2 font-display">{a.senderName} sent {a.name} {a.combo > 1 ? `x${a.combo}` : ""}</span>
              </div>
            </div>
          );
        }
        if (a.tier === "luxury" || a.tier === "legendary") {
          return (
            <div key={a.key} className="absolute inset-0 flex items-center justify-center bg-black/50">
              <div className="rays-spin absolute w-[600px] h-[600px] opacity-30"
                style={{ background: "conic-gradient(from 0deg, transparent 0deg, #EC4899 20deg, transparent 40deg, transparent 90deg, #7C3AED 110deg, transparent 130deg, transparent 180deg, #EC4899 200deg, transparent 220deg, transparent 270deg, #7C3AED 290deg, transparent 310deg)" }}
              />
              <div className="gift-luxury flex flex-col items-center relative">
                <GiftIcon a={a} className={`${a.tier === "legendary" ? "w-52 h-52" : "w-44 h-44"} object-contain`} emojiClass={a.tier === "legendary" ? "text-9xl" : "text-8xl"} style={{ filter: "drop-shadow(0 0 40px rgba(255,215,0,0.8))" }} />
                <span className="gradient-text text-xl font-extrabold mt-3 font-display">{a.name} {a.combo > 1 ? `x${a.combo}` : ""}</span>
                <span className="text-white/90 text-sm font-semibold">from {a.senderName}</span>
              </div>
            </div>
          );
        }
        return null;
      })}
    </div>
  );
}

export function MarqueeBanner({ marquee }) {
  if (!marquee) return null;
  return (
    <div className="absolute top-16 left-0 right-0 z-40 overflow-hidden pointer-events-none">
      <div key={marquee.key} className="marquee-banner inline-block whitespace-nowrap gradient-primary rounded-full px-4 py-1.5 text-white text-xs font-bold shadow-[0_0_25px_rgba(236,72,153,0.6)]">
        {marquee.text}
      </div>
    </div>
  );
}

function EntryNameBanner({ entry, color, prefix = "👑" }) {
  const badge = tierImage(entry.tier);
  return (
    <div className="absolute top-24 left-0 right-0 flex justify-center overflow-visible pointer-events-none">
      <div className="entry-sweep relative flex items-center justify-center" style={{ width: 320, height: 96 }}>
        <img src="/assets/ribbon-gold.png" alt="" className="absolute inset-0 w-full h-full object-contain drop-shadow-[0_4px_18px_rgba(230,184,76,0.5)]" />
        <span className="relative z-10 text-[11px] font-extrabold flex items-center gap-1 -mt-2" style={{ transform: "rotate(-2.5deg)", color: "#5C3A00" }}>
          {badge ? <img src={badge} alt="" className="w-5 h-5 object-contain drop-shadow-[0_1px_3px_rgba(0,0,0,0.4)]" /> : <span>{prefix}</span>}
          <span className="font-royal">{entry.tier} {entry.name}</span>
          <span className="opacity-80">has entered</span>
        </span>
      </div>
    </div>
  );
}

function TierBadgeFlash({ entry, color }) {
  const img = tierImage(entry.tier);
  if (!img) return null;
  return (
    <div className="absolute inset-x-0 top-[36%] flex flex-col items-center pointer-events-none" data-testid="tier-badge-flash">
      <div className="badge-trail absolute inset-x-0 -top-4 h-32" style={{ background: `linear-gradient(90deg, transparent, ${color}3D, transparent)` }} />
      <img src={img} alt={entry.tier} className="badge-flash w-28 h-28 object-contain"
        style={{ filter: `drop-shadow(0 0 26px ${color}) drop-shadow(0 5px 12px rgba(0,0,0,0.65))` }} />
    </div>
  );
}

const SHARDS = Array.from({ length: 14 });

export function EntryEffectLayer({ entry }) {
  if (!entry) return null;
  const color = entry.tier_color || TIER_COLORS[entry.tier] || "#7C3AED";

  if (!entry.premium) {
    if (!entry.entry_message) return null;
    return (
      <div key={entry.key} className="absolute inset-0 z-40 pointer-events-none">
        <EntryNameBanner entry={entry} color={color} prefix="✨" />
      </div>
    );
  }

  const effect = entry.effect || "golden_sweep";

  return (
    <div key={entry.key} className="absolute inset-0 z-40 pointer-events-none overflow-hidden" data-testid="entry-effect">
      <TierBadgeFlash entry={entry} color={color} />
      {effect === "golden_sweep" && (
        <>
          <div className="beam-sweep absolute inset-y-0 w-1/2" style={{ background: "linear-gradient(90deg, transparent, rgba(255,215,0,0.45), rgba(255,255,255,0.25), transparent)" }} />
          <EntryNameBanner entry={entry} color="#FFD700" prefix="🌟" />
        </>
      )}
      {effect === "diamond_shatter" && (
        <>
          <div className="flash-pulse absolute inset-0" style={{ background: "radial-gradient(circle at center, rgba(185,242,255,0.35), transparent 65%)" }} />
          <div className="absolute inset-0 flex items-center justify-center">
            {SHARDS.map((_, i) => {
              const ang = (i / SHARDS.length) * Math.PI * 2;
              return (
                <span key={i} className="shatter-fly absolute text-3xl"
                  style={{ "--dx": `${Math.cos(ang) * 170}px`, "--dy": `${Math.sin(ang) * 170}px`, "--rot": `${(i % 2 ? 1 : -1) * 200}deg`, animationDelay: `${i * 0.04}s` }}>
                  💎
                </span>
              );
            })}
          </div>
          <EntryNameBanner entry={entry} color="#B9F2FF" prefix="💎" />
        </>
      )}
      {effect === "royal_cinematic" && (
        <>
          <div className="flash-pulse absolute inset-0 bg-black/60" />
          <div className="carpet-rise absolute bottom-0 left-0 right-0 h-1/2" style={{ background: "linear-gradient(to top, rgba(220,20,60,0.55), transparent)" }} />
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="crown-drop text-7xl" style={{ filter: "drop-shadow(0 0 30px rgba(255,77,109,0.9))" }}>👑</span>
            <span className="gift-pop gradient-text text-xl font-extrabold font-display mt-2">ROYAL ENTRANCE</span>
          </div>
          <EntryNameBanner entry={entry} color="#FF4D6D" prefix="👑" />
        </>
      )}
      {effect === "neon_storm" && (
        <>
          <div className="flash-pulse absolute inset-0" style={{ background: "radial-gradient(circle at 30% 30%, rgba(124,58,237,0.4), transparent 55%), radial-gradient(circle at 70% 60%, rgba(236,72,153,0.4), transparent 55%)" }} />
          {[15, 45, 70].map((left, i) => (
            <span key={left} className="rise-float absolute bottom-1/3 text-5xl" style={{ left: `${left}%`, animationDelay: `${i * 0.25}s`, filter: "drop-shadow(0 0 16px #7C3AED)" }}>⚡</span>
          ))}
          <EntryNameBanner entry={entry} color="#A78BFA" prefix="⚡" />
        </>
      )}
      {effect === "fire_wave" && (
        <>
          {[5, 20, 35, 50, 65, 80].map((left, i) => (
            <span key={left} className="rise-float absolute bottom-0 text-4xl" style={{ left: `${left}%`, animationDelay: `${i * 0.12}s`, filter: "drop-shadow(0 0 14px #F59E0B)" }}>🔥</span>
          ))}
          <EntryNameBanner entry={entry} color="#F59E0B" prefix="🔥" />
        </>
      )}
      {effect === "butterfly_bloom" && (
        <>
          {[10, 30, 55, 75, 45].map((left, i) => (
            <span key={left} className="rise-float absolute bottom-8 text-4xl" style={{ left: `${left}%`, animationDelay: `${i * 0.2}s` }}>🦋</span>
          ))}
          <EntryNameBanner entry={entry} color="#F0ABFC" prefix="🦋" />
        </>
      )}
      {effect === "galaxy_warp" && (
        <>
          <div className="flash-pulse absolute inset-0" style={{ background: "radial-gradient(circle at center, rgba(124,58,237,0.5), rgba(14,14,18,0.3) 70%)" }} />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="rays-spin text-8xl opacity-80">🌌</span>
          </div>
          {[20, 50, 75].map((left, i) => (
            <span key={left} className="rise-float absolute bottom-1/4 text-3xl" style={{ left: `${left}%`, animationDelay: `${i * 0.3}s` }}>✨</span>
          ))}
          <EntryNameBanner entry={entry} color="#C4B5FD" prefix="🌌" />
        </>
      )}
    </div>
  );
}

export function LevelUpBanner({ levelUp }) {
  if (!levelUp) return null;
  return (
    <div className="absolute inset-0 z-40 flex items-center justify-center pointer-events-none">
      <div key={levelUp.key} className="gift-pop flex flex-col items-center">
        <span className="text-6xl">🎖️</span>
        <span className="gradient-text text-lg font-extrabold font-display">LEVEL UP!</span>
        <span className="text-white text-sm font-semibold">{levelUp.name} reached Lv.{levelUp.level}</span>
      </div>
    </div>
  );
}

export function HeartLayer({ hearts }) {
  const emojis = ["❤️", "💖", "💜", "🩷", "💕"];
  return (
    <div className="absolute bottom-28 right-4 w-16 h-72 pointer-events-none z-30">
      {hearts.map((h) => (
        <span key={h} className="heart-float absolute bottom-0 text-2xl" style={{ right: `${(h % 4) * 10}px` }}>
          {emojis[h % emojis.length]}
        </span>
      ))}
    </div>
  );
}
